import { autoinject, bindingMode } from "aurelia-framework";
import { bindable } from 'aurelia-typed-observable-plugin';
import { AuthPageContext } from 'app/um/lib/auth-page-context';
import { ComponentBase, Sorting, SortDirection } from "app/shared/lib/essette-site";
import { deepClone } from 'app/shared/lib/utilities';
import { PreServiceDayService } from 'app/um/services/pre-service-day-service';
import { IPopup } from 'app/shared/elements/popup-base';
import { FormSuccessCustomEvent } from 'app/shared/elements/forms/ess-form';
import { apiCall } from 'app/shared/lib/api/api';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { validate } from 'app/shared/elements/ess-rule';
import * as UM from 'app/um/models/essette-api';
import { guid } from 'app/shared/lib/guid';


@autoinject()
export class CareDaysGrid extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable.objectNull({ defaultBindingMode: bindingMode.toView, changeHandler: 'reloadCareDays' })
    public auth: UM.Auth | null = null;
    @bindable.boolean({ defaultBindingMode: bindingMode.toView })
    public readonly: boolean = true;
    @bindable.boolean({ defaultBindingMode: bindingMode.toView })
    public minimal: boolean = false;

    protected days: UM.AdmissionPreServiceDay[] = [];
    protected saveCareDay: UM.AdmissionPreServiceDay | null = null;

    private sorting: Sorting = new Sorting();
    protected resultCount: number | null = 0;
    protected pageSize: number = 25;

    protected $isValid: boolean = true;
    protected validate = validate;


    protected id: guid = guid();

    ////****************** Element References ***************************************////
    protected $modal!: IPopup;
    protected $pager!: EssPagerCustomElement;


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly preServiceDaysService: PreServiceDayService,
        private readonly context: AuthPageContext,
        ...rest: any[]) {

        super(...rest);
    }

    protected bind(bindingContext: Object, overrideContext: Object): void {
        if(!this.auth) {
            this.auth = this.context.auth;
        }
        this.reloadCareDays();
    }



    ////****************** Events ***************************************************////
    private edit(day: UM.AdmissionPreServiceDay): void {
        if(!day || !this.$modal) {
            return;
        }

        const careDay = deepClone(day);
        if(!careDay.determination?.isActive) {
            careDay.determination = null;
        }

        this.saveCareDay = careDay;
        this.$modal.open();
    }

    private add(): void {
        if(!this.$modal) {
            return;
        }

        this.saveCareDay = <UM.AdmissionPreServiceDay>{ id: null };
        this.$modal.open();
    }

    ////****************** Functions ************************************************////
    /**
     * Handles the grid's sort event
     */
    protected applySort(event: SortChangeEvent): void {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        this.reloadCareDays();
    }

    private async reloadCareDays(): Promise<boolean> {
        if(this.auth && this.auth.id) {
            const days = await this.loadCareDays(this.auth.id) as UM.AdmissionPreServiceDay[];

            if(!this.auth.admission) {
                this.auth.admission = {}
            }

            this.days = days
            this.auth.admission.careDays = days;
            return true;
        }
        return false;
    }

    protected async formSuccess({ detail }: FormSuccessCustomEvent): Promise<void> {
        if(!!this.$modal) {
            this.$modal.close();
            this.reloadCareDays();
        }
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadCareDays(authId: string): Promise<UM.AdmissionPreServiceDay[]> {
        if(!authId) { return []; }

        const options: Partial<IQueryLoadOptions> = {
            top: this.pageSize,
            page: this.$pager?.page || 0,
            sorting: this.sorting
        };

        const includes: string[] = [
            'levelOfCare.actual',
            'levelOfCare.approved',
            'determination'
        ];

        const fields: string[] = [
            'quantity',
            'levelOfCare.actual.description',
            'determination.description',
            'levelOfCare.approved.description',
            'auth.id'
        ];

        try {
            this.state.beginLoad();
            const response = await this.preServiceDaysService.loadAdmissionPreServiceDays(authId, options, includes, fields);
            var r = response.results;
            this.resultCount = response.count || 0;
            return r;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }
}

